"use client";

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useLikePost from "../../../hooks/useLikePost";
import useComments from "../../../hooks/useComments";
import "./PostCard.css";
import { useUserInformation } from "../../../../Profile/hooks/useUserInformation";
import { resolveDoctorAvatar } from "../../../../ProfileDoctor/hooks/useDoctorInformation";
import { fetchUserById } from "../../../../Profile/redux/thunks/userThunks";
import { fetchDoctors } from "../../../../authDoctor/redux/thunks/doctorsThunks";
import { DOCTOR_ROUTES } from "../../../../routes/patientRoutes/routePaths";
import likeIconOutline from "../../../../assets/icons/svg/postcard-like-outline.svg";
import likeIconFilled from "../../../../assets/icons/svg/postcard-like-filled.svg";
import commentIconPrimary from "../../../../assets/icons/svg/postcard-comment-primary.svg";
import commentIconMuted from "../../../../assets/icons/svg/postcard-comment-muted.svg";
import sendIcon from "../../../../assets/icons/svg/postcard-send.svg";
import AIResponseBanner from "../../shared/AIResponseBanner/AIResponseBanner";

const DEFAULT_AVATAR = "https://cdn-icons-png.flaticon.com/512/149/149071.png";
const DOCTOR_DEFAULT_AVATAR = "https://cdn-icons-png.flaticon.com/512/3774/3774299.png";

/* ================= Helpers ================= */
const pick = (...vals) =>
  vals.find((v) => {
    if (v == null) return false;
    const s = String(v).trim();
    return s.length > 0 && s.toLowerCase() !== "undefined" && s.toLowerCase() !== "null";
  });

const safeStr = (v) => (v == null ? "" : String(v)).trim();

const isDoctorLike = (role) => {
  const r = String(role || "").toLowerCase();
  return r.includes("doctor") || r.includes("médecin") || r.includes("medecin") || r.includes("dr");
};

const isNumericId = (v) => /^\d+$/.test(String(v ?? "").trim());

const MAX_DATA_URL_LEN = 2_000_000;

const getMediaUrl = (photo) => {
  if (!photo) return null;
  try {
    const s = String(photo).trim();
    if (s.startsWith("data:image/")) {
      if (s.length < 50 || s.length > MAX_DATA_URL_LEN) return null;
      return s;
    }
    if (s.startsWith("http://") || s.startsWith("https://")) return s;
    if (/^[A-Za-z0-9+/]+=*$/.test(s) && s.length > 100 && s.length < MAX_DATA_URL_LEN) {
      const clean = s.replace(/[^A-Za-z0-9+/=]/g, "");
      atob(clean.slice(0, 64));
      return `data:image/jpeg;base64,${clean}`;
    }
    return null;
  } catch {
    return null;
  }
};

const isDefaultAvatarLike = (value) => {
  const s = safeStr(value).toLowerCase();
  if (!s) return true;

  return (
    s === DEFAULT_AVATAR.toLowerCase() ||
    s === DOCTOR_DEFAULT_AVATAR.toLowerCase() ||
    s.includes("149071.png") ||
    s.includes("3774299.png") ||
    s.includes("default") ||
    s.includes("placeholder") ||
    s.includes("avatar-default") ||
    s.includes("user.png") ||
    s.includes("doctor.png") ||
    s.includes("unknown") ||
    s.includes("inconnu")
  );
};

const getInitials = (name, fallback = "U") => {
  const clean = safeStr(name);
  if (!clean) return fallback;

  // Supprime le préfixe "Dr." / "Dr " / "dr." pour ne pas le compter comme initiale
  const stripped = clean.replace(/^dr\.?\s*/i, "").trim();

  const parts = stripped.split(/\s+/).filter(Boolean);
  if (!parts.length) return fallback;

  const first = parts[0]?.charAt(0) || "";
  const last = parts.length > 1 ? parts[parts.length - 1]?.charAt(0) : "";

  return (first + last).toUpperCase() || fallback;
};

/* ================= timeAgo ================= */
const timeAgo = (d, t) => {
  const raw = d ?? null;
  if (!raw) return t?.("postCard.now") || "maintenant";

  const date = new Date(raw);
  if (isNaN(date.getTime())) return t?.("postCard.now") || "maintenant";

  const secs = Math.floor((Date.now() - date.getTime()) / 1000);

  if (secs < 5) return t?.("postCard.now") || "maintenant";

  const map = [
    ["an",   31536000],
    ["mois", 2592000],
    ["sem",  604800],
    ["j",    86400],
    ["h",    3600],
    ["min",  60],
    ["s",    1],
  ];

  for (const [k, v] of map) {
    const n = Math.floor(secs / v);
    if (n >= 1) {
      if (k === "an") return `il y a ${n} an${n > 1 ? "s" : ""}`;
      if (k === "mois") return `il y a ${n} mois`;
      if (k === "sem") return `il y a ${n} sem${n > 1 ? "s" : ""}`;
      return `il y a ${n}${k}`;
    }
  }

  return t?.("postCard.now") || "maintenant";
};

/* ================= resolveDate ================= */
const resolveDate = (obj) => {
  if (!obj) return null;
  return (
    obj.createdAt ??
    obj.created_at ??
    obj.publishedAt ??
    obj.published_at ??
    obj.date ??
    obj.timestamp ??
    obj.time ??
    obj.updatedAt ??
    null
  );
};

/* ===== SafeImage ===== */
const SafeImage = ({ debugTag, src, alt, className, onClick, style }) => {
  const [err, setErr] = useState(false);

  const finalSrc = useMemo(() => {
    const media = getMediaUrl(src);
    if (!media) return null;
    if (isDefaultAvatarLike(media)) return null;
    return media;
  }, [src]);

  useEffect(() => {
    setErr(false);
  }, [finalSrc]);

  if (!finalSrc || err) return null;

  return (
    <img
      className={className}
      style={style}
      src={finalSrc}
      alt={alt || "image"}
      onClick={onClick}
      loading="lazy"
      onLoad={() => undefined}
      onError={() => {
        setErr(true);
      }}
    />
  );
};

/* ===== Actor extraction ===== */
const extractCommentActor = (c) => {
  const doctorId =
    c?.doctorId ??
    c?.doctor_id ??
    c?.doctor?.id ??
    c?.doctor?._id ??
    c?.doctor?.doctorId ??
    c?.doctor?.pgId ??
    c?.author?.doctorId ??
    c?.author?.id ??
    null;

  const embeddedDoctor = c?.doctor ?? c?.Doctor ?? c?.author?.doctor ?? c?.author ?? null;

  const authorRole = c?.author?.role ?? c?.doctor?.role ?? c?.user?.role ?? c?.role ?? null;
  const isDoctorByRole = authorRole && isDoctorLike(authorRole);

  if (doctorId != null || embeddedDoctor != null || isDoctorByRole) {
    return { type: "doctor", id: doctorId != null ? String(doctorId) : null, embedded: embeddedDoctor };
  }

  const uid =
    c?.userId ??
    c?.user_id ??
    c?.authorId ??
    c?.createdById ??
    c?.user?.id ??
    c?.user?._id ??
    c?.user?.userId ??
    c?.author?.id ??
    null;

  const embeddedUser = c?.user ?? c?.User ?? c?.author ?? null;

  return { type: "user", id: uid != null ? String(uid) : null, embedded: embeddedUser };
};

/* ===== Build names ===== */
const buildDoctorName = (obj, fallback = "Doctor") => {
  if (!obj) return fallback;
  const name = pick(
    obj.fullName,
    obj.name,
    obj.doctorName,
    obj.firstName && obj.lastName ? `${obj.firstName} ${obj.lastName}`.trim() : null,
    obj.user?.fullName,
    obj.user?.firstName && obj.user?.lastName ? `${obj.user.firstName} ${obj.user.lastName}`.trim() : null,
    obj.firstName,
    obj.lastName,
    obj.user?.firstName,
    obj.user?.lastName
  );
  if (!name) return fallback;
  const n = String(name).trim();
  return n.toLowerCase().startsWith("dr") ? n : `Dr. ${n}`;
};

const buildUserName = (obj, fallback = "Member") => {
  if (!obj) return fallback;
  const name = pick(
    obj.fullName,
    obj.name,
    obj.firstName && obj.lastName ? `${obj.firstName} ${obj.lastName}`.trim() : null,
    obj.firstName,
    obj.lastName,
    obj.user?.fullName,
    obj.user?.firstName && obj.user?.lastName ? `${obj.user.firstName} ${obj.user.lastName}`.trim() : null
  );
  return name || fallback;
};

const resolveDoctorAvatarLikeProvidersList = (doc) => {
  if (!doc || typeof doc !== "object") return null;

  const raw = doc?.avatarUrl ?? doc?.avatar ?? doc?.photo ?? doc?.image ?? null;
  if (!raw) return null;
  if (isNumericId(raw)) return null;
  if (isDefaultAvatarLike(raw)) return null;

  const resolved = resolveDoctorAvatar(raw);
  if (!resolved || isDefaultAvatarLike(resolved)) return null;

  return resolved;
};

const resolvePatientAvatar = (hookAvatarSrc, obj) => {
  const fromHook = getMediaUrl(hookAvatarSrc);
  if (fromHook && !isDefaultAvatarLike(fromHook)) return fromHook;

  const raw = pick(
    obj?.avatar,
    obj?.avatarUrl,
    obj?.photo,
    obj?.profileImage,
    obj?.profile_image,
    obj?.picture
  );

  const finalUrl = getMediaUrl(raw);
  if (!finalUrl || isDefaultAvatarLike(finalUrl)) return null;

  return finalUrl;
};

const InitialsAvatar = ({ name, className = "", size = 36, fallback = "U" }) => (
  <div
    className={className}
    style={{
      background: "linear-gradient(135deg, #5B8DEE 0%, #7AA7F7 100%)",
      color: "#ffffff",
      display: "grid",
      placeItems: "center",
      fontWeight: "700",
      fontSize: "16px",
      borderRadius: "50%",
      width: `${size}px`,
      height: `${size}px`,
      minWidth: `${size}px`,
      textTransform: "uppercase",
      userSelect: "none",
      boxShadow: "0 8px 18px rgba(91, 141, 238, 0.22)",
    }}
  >
    {getInitials(name, fallback)}
  </div>
);

/* ================= DeleteConfirmModal ================= */
const DeleteConfirmModal = ({ onConfirm, onCancel, t, title, message, isLoading = false }) => (
  <div
    className="pcard-modal-backdrop"
    onClick={onCancel}
    role="dialog"
    aria-modal="true"
    aria-labelledby="delete-modal-title"
  >
    <div className="pcard-modal" onClick={(e) => e.stopPropagation()}>
      {/* Icône */}
      <div className="pcard-modal-icon">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6"
            stroke="#ef4444"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M10 11v5M14 11v5"
            stroke="#ef4444"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      </div>

      <h3 id="delete-modal-title">
        {title || t?.("posts.deleteCommentTitle") || "Supprimer le commentaire"}
      </h3>
      {message && <p>{message}</p>}

      <div className="pcard-modal-actions">
        <button
          type="button"
          className="pcard-modal-btn pcard-modal-btn--ghost"
          onClick={onCancel}
        >
          {t?.("common.cancel") || "Annuler"}
        </button>
        <button
          type="button"
          className="pcard-modal-btn pcard-modal-btn--danger"
          onClick={onConfirm}
          disabled={isLoading}
        >
          {isLoading ? t?.("common.loading") || "Chargement…" : t?.("common.delete") || "Supprimer"}
        </button>
      </div>
    </div>
  </div>
);

/* ================= CommentRow ================= */
function CommentRow({
  c,
  actor,
  authorObj,
  embeddedData,
  isDoctorAuthor,
  t,
  viewerId,
  openMenuId,
  setOpenMenuId,
  editingId,
  editingValue,
  setEditingValue,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
  onDoctorClick,
}) {
  const navigate = useNavigate();
  const commentId = String(c?._id || c?.id || "");

  const isOwner = viewerId && actor?.id && String(actor.id) === String(viewerId);
  const isEditing = editingId && String(editingId) === commentId;

  const effectiveAuthor = useMemo(() => {
    const a = authorObj && Object.keys(authorObj).length ? authorObj : null;
    const e = embeddedData && typeof embeddedData === "object" ? embeddedData : null;
    const direct = isDoctorAuthor ? c?.doctor ?? c?.Doctor ?? c?.author : c?.user ?? c?.User ?? c?.author;
    return a || e || (typeof direct === "object" ? direct : null) || null;
  }, [authorObj, embeddedData, c, isDoctorAuthor]);

  const { avatarSrc: hookAvatarSrc } = useUserInformation(isDoctorAuthor ? null : effectiveAuthor);

  const authorName = useMemo(() => {
    return isDoctorAuthor
      ? buildDoctorName(effectiveAuthor, t?.("postCard.doctor") || "Doctor")
      : buildUserName(effectiveAuthor, t?.("postCard.user") || "User");
  }, [effectiveAuthor, isDoctorAuthor, t]);

  const roleLabel = useMemo(
    () => (isDoctorAuthor ? "Doctor" : effectiveAuthor?.role || effectiveAuthor?.user?.role || "Patient"),
    [isDoctorAuthor, effectiveAuthor]
  );

  const avatarFinal = useMemo(() => {
    if (isDoctorAuthor) {
      return resolveDoctorAvatarLikeProvidersList(effectiveAuthor || actor?.embedded || null);
    }
    const patient = effectiveAuthor || c?.user || c?.User || c?.author || actor?.embedded;
    return resolvePatientAvatar(hookAvatarSrc, patient);
  }, [c, actor, isDoctorAuthor, effectiveAuthor, hookAvatarSrc]);

  const doctorProfileId = useMemo(() => {
    if (!isDoctorAuthor) return null;
    return pick(
      actor?.id,
      effectiveAuthor?.id,
      effectiveAuthor?._id,
      effectiveAuthor?.doctorId,
      effectiveAuthor?.pgId,
      c?.doctorId
    );
  }, [isDoctorAuthor, actor, effectiveAuthor, c]);

  const handleDoctorClick = useCallback(() => {
    if (!isDoctorAuthor || !doctorProfileId) return;
    if (typeof onDoctorClick === "function") {
      onDoctorClick(doctorProfileId);
    } else {
      navigate(DOCTOR_ROUTES.PROFILE_DOCTOR(doctorProfileId));
    }
  }, [isDoctorAuthor, doctorProfileId, onDoctorClick, navigate]);

  const isClickable = isDoctorAuthor && !!doctorProfileId;

  const commentDate = resolveDate(c);

  return (
    <div className="pcc-item">
      <div className="pcc-left">
        {avatarFinal ? (
          <SafeImage
            debugTag={`comment-avatar-${commentId || "x"}`}
            src={avatarFinal}
            alt={authorName}
            className={`pcc-av${isClickable ? " pcc-av--clickable" : ""}`}
            style={isClickable ? { cursor: "pointer" } : undefined}
            onClick={isClickable ? handleDoctorClick : undefined}
          />
        ) : (
          <InitialsAvatar
            name={authorName}
            fallback={isDoctorAuthor ? "D" : "U"}
            className={`pcc-av pcc-av-default${isClickable ? " pcc-av--clickable" : ""}`}
            size={40}
          />
        )}
      </div>

      <div className="pcc-body">
        <div className="pcc-header">
          <div className="pcc-info">
            {isClickable ? (
              <button
                type="button"
                className="pcc-name pcc-name-doctor pcc-name--link"
                onClick={handleDoctorClick}
                title={`View ${authorName}'s profile`}
              >
                {authorName}
              </button>
            ) : (
              <span className={`pcc-name ${isDoctorAuthor ? "pcc-name-doctor" : ""}`}>{authorName}</span>
            )}

            <span className={`pcc-role ${isDoctorAuthor ? "pcc-role-doctor" : ""}`}>{roleLabel}</span>
            <span className="pcc-time">{timeAgo(commentDate, t)}</span>
          </div>

          {isOwner && !!commentId && (
            <div className="pcc-menu-wrapper" data-comment-menu={commentId}>
              <button
                type="button"
                className="pcc-menu-btn"
                aria-haspopup="menu"
                aria-expanded={openMenuId === commentId}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenMenuId((prev) => (prev === commentId ? null : commentId));
                }}
                title={t?.("postCard.options") || "Options"}
              >
                •••
              </button>

              <div className={`pcc-menu ${openMenuId === commentId ? "open" : ""}`} role="menu">
                <button
                  className="pcc-menu-item"
                  role="menuitem"
                  onClick={() => onStartEdit(commentId, c?.content || c?.text || "")}
                >
                  {t?.("postCard.edit") || "Edit"}
                </button>
                <button
                  className="pcc-menu-item danger"
                  role="menuitem"
                  onClick={() => onDelete(commentId)}
                >
                  {t?.("postCard.delete") || "Delete"}
                </button>
              </div>
            </div>
          )}
        </div>

        {isEditing ? (
          <div className="pcc-edit">
            <textarea
              className="pcc-edit-input"
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              rows={2}
              autoFocus
            />
            <div className="pcc-edit-actions">
              <button
                type="button"
                className="pcc-edit-btn"
                onClick={() => onSaveEdit(commentId)}
                disabled={!safeStr(editingValue)}
              >
                {t?.("common.save") || "Save"}
              </button>
              <button type="button" className="pcc-edit-btn ghost" onClick={onCancelEdit}>
                {t?.("common.cancel") || "Cancel"}
              </button>
            </div>
          </div>
        ) : (
          <div className="pcc-text">{c?.content || c?.text || ""}</div>
        )}
      </div>
    </div>
  );
}

/* ================= Main Component ================= */
export default function PostCard({ post, onTranslate, currentUserId, onEdit, onDelete }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const postId = post?.id_post ?? post?.postId ?? post?.post_id ?? post?._id ?? undefined;
  const postUserId =
    post?.user_id ??
    post?.userId ??
    post?.user?.id ??
    post?.user?._id ??
    post?.user?.userId ??
    post?.user?.user_id ??
    null;
  const postOwnerId =
    postUserId ??
    post?.authorId ??
    post?.author_id ??
    post?.author?.id ??
    post?.author?._id ??
    post?.author?.userId ??
    post?.author?.user_id ??
    null;
  const aiResponseText = post?.aiResponseText ?? post?.ai_response ?? post?.aiResponse ?? "";
  const viewerIdValue =
    currentUserId && typeof currentUserId === "object"
      ? currentUserId.id ?? currentUserId._id ?? currentUserId.userId
      : currentUserId;
  const viewerId = viewerIdValue != null ? String(viewerIdValue) : "";
  const isPostOwner =
    !!viewerId && postOwnerId != null && String(postOwnerId) === viewerId;

  const usersMap = useSelector((state) => state.user?.users || {});
  const doctorsFromRedux = useSelector(
    (state) => state.doctors?.doctors || state.doctors?.items || state.doctor?.doctors || []
  );

  const doctorsMap = useMemo(() => {
    if (doctorsFromRedux && !Array.isArray(doctorsFromRedux) && typeof doctorsFromRedux === "object") {
      return doctorsFromRedux;
    }
    if (Array.isArray(doctorsFromRedux)) {
      const map = {};
      doctorsFromRedux.forEach((doc) => {
        const id = String(doc?.id || doc?._id || doc?.doctorId || "");
        if (id) map[id] = doc;
      });
      return map;
    }
    return {};
  }, [doctorsFromRedux]);

  const fetchedUser = postUserId != null ? usersMap[String(postUserId)] : null;

  const fetchInitiatedRef = useRef(new Set());
  const hasPrefetchedRef = useRef(false);
  const doctorsFetchedRef = useRef(false);
  const commentsIdsRef = useRef("");

  const resolvedUserObject = useMemo(() => {
    if (fetchedUser) return fetchedUser;
    if (post?.user) return post.user;
    return null;
  }, [fetchedUser, post?.user]);

  const { avatarSrc: hookAvatarSrc } = useUserInformation(resolvedUserObject);

  const [showTranslation, setShowTranslation] = useState(false);
  const [translatedText, setTranslatedText] = useState("");
  const [translating, setTranslating] = useState(false);
  const [translationError, setTranslationError] = useState("");
  const [showComments, setShowComments] = useState(false);
  const [commentValue, setCommentValue] = useState("");
  const [localComments, setLocalComments] = useState([]);
  const [showAllComments, setShowAllComments] = useState(false);

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [editingPost, setEditingPost] = useState(false);
  const [editingPostValue, setEditingPostValue] = useState("");
  const [savingPost, setSavingPost] = useState(false);
  const [postDeleteConfirm, setPostDeleteConfirm] = useState(false);
  const [deletingPost, setDeletingPost] = useState(false);

  // ── State pour la modal de confirmation de suppression ──
  const [deleteConfirm, setDeleteConfirm] = useState(null); // null ou l'id du commentaire

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e) => {
      if (!menuRef.current?.contains(e.target)) setMenuOpen(false);
    };
    const onEsc = (e) => e.key === "Escape" && setMenuOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [menuOpen]);

  const [openCommentMenuId, setOpenCommentMenuId] = useState(null);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  useEffect(() => {
    if (!openCommentMenuId) return;
    const onDown = (e) => {
      if (!e.target.closest?.(`[data-comment-menu="${openCommentMenuId}"]`)) setOpenCommentMenuId(null);
    };
    const onEsc = (e) => e.key === "Escape" && setOpenCommentMenuId(null);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [openCommentMenuId]);

  // Fermer la modal sur Escape
  useEffect(() => {
    if (!deleteConfirm && !postDeleteConfirm) return;
    const onEsc = (e) => {
      if (e.key !== "Escape") return;
      setDeleteConfirm(null);
      setPostDeleteConfirm(false);
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [deleteConfirm, postDeleteConfirm]);

  const {
    comments,
    isLoading: loadingComments,
    commentsCount: totalComments,
    fetchComments,
    addComment,
    editComment,
    removeComment,
  } = useComments(postId);

  useEffect(() => {
    setLocalComments(Array.isArray(comments) ? comments : []);
  }, [comments]);

  const userId = currentUserId;
  const { liked, count: likeCount, handleToggleLike, fetchStatus } = useLikePost(postId, userId, postOwnerId);

  useEffect(() => {
    if (doctorsFetchedRef.current) return;
    doctorsFetchedRef.current = true;
    dispatch(fetchDoctors()).catch(() => {});
  }, [dispatch]);

  useEffect(() => {
    if (!postUserId) return;
    const key = `user:${String(postUserId)}`;
    if (fetchInitiatedRef.current.has(key)) return;
    const isIncomplete = !post?.user?.firstName || !post?.user?.role;
    const notInRedux = !fetchedUser;
    if (isIncomplete || notInRedux) {
      fetchInitiatedRef.current.add(key);
      dispatch(fetchUserById(postUserId));
    }
  }, [dispatch, postUserId, post?.user, fetchedUser]);

  useEffect(() => {
    if (!Array.isArray(localComments) || localComments.length === 0) return;

    const currentIds = localComments
      .map((c) => c?._id || c?.id)
      .filter(Boolean)
      .sort()
      .join(",");

    if (commentsIdsRef.current === currentIds) return;
    commentsIdsRef.current = currentIds;

    const usersToFetch = new Set();

    for (const c of localComments) {
      const actor = extractCommentActor(c);
      if (actor.type === "user") {
        const uid = actor?.id ? String(actor.id) : null;
        if (!uid) continue;
        const key = `user:${uid}`;
        if (usersMap?.[uid] || fetchInitiatedRef.current.has(key)) continue;
        usersToFetch.add(uid);
      }
    }

    usersToFetch.forEach((uid) => {
      fetchInitiatedRef.current.add(`user:${uid}`);
      dispatch(fetchUserById(uid));
    });
  }, [localComments, dispatch, usersMap]);

  const author = useMemo(() => {
    const u = resolvedUserObject;
    if (!u) {
      return {
        name: `${t?.("postCard.user") || "User"} ${postUserId || ""}`.trim(),
        role: post?.role,
      };
    }

    const full =
      (u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : "") ||
      u.firstName ||
      u.lastName ||
      u.fullName ||
      u.name ||
      t?.("postCard.user") ||
      "User";

    return { name: String(full).trim(), role: u.role || post?.role };
  }, [resolvedUserObject, post?.role, postUserId, t]);

  const isDoctorPostAuthor = useMemo(() => isDoctorLike(author.role), [author.role]);

  const authorAvatar = useMemo(() => {
    if (isDoctorPostAuthor) return resolveDoctorAvatarLikeProvidersList(resolvedUserObject);
    return resolvePatientAvatar(hookAvatarSrc, resolvedUserObject);
  }, [isDoctorPostAuthor, resolvedUserObject, hookAvatarSrc]);

  const mediaUrl = useMemo(() => getMediaUrl(post?.photo), [post?.photo]);
  const originalText = post?.description || t?.("postCard.noDescription") || "No description";

  const postDate = resolveDate(post);

  useEffect(() => {
    if (postId && userId) fetchStatus();
  }, [postId, userId, fetchStatus]);

  useEffect(() => {
    if (!showComments || !postId) return;
    fetchComments();
  }, [showComments, postId, fetchComments]);

  useEffect(() => {
    if (!showComments) setShowAllComments(false);
  }, [showComments]);

  useEffect(() => {
    if (!postId || hasPrefetchedRef.current) return;
    const shouldPrefetch = post?.commentsCount == null && (!localComments || localComments.length === 0);
    if (shouldPrefetch) {
      hasPrefetchedRef.current = true;
      fetchComments().catch(() => {});
    }
  }, [postId, post?.commentsCount, localComments, fetchComments]);

  const displayCommentsCount = Number(
    (post?.commentsCount ?? totalComments ?? (Array.isArray(localComments) ? localComments.length : 0)) ?? 0
  );

  const orderedComments = useMemo(() => {
    const arr = Array.isArray(localComments) ? [...localComments] : [];
    return arr.sort((a, b) => {
      const actorOrder = Number(extractCommentActor(b).type === "doctor") -
        Number(extractCommentActor(a).type === "doctor");
      if (actorOrder !== 0) return actorOrder;

      const rawDateA = new Date(resolveDate(a) || 0).getTime();
      const rawDateB = new Date(resolveDate(b) || 0).getTime();
      const da = Number.isFinite(rawDateA) ? rawDateA : 0;
      const db = Number.isFinite(rawDateB) ? rawDateB : 0;
      return db - da;
    });
  }, [localComments]);

  const hasMoreThanThreeComments = orderedComments.length > 3;
  const visibleComments = showAllComments ? orderedComments : orderedComments.slice(0, 3);

  const handleLikeClick = useCallback(
    async (e) => {
      e.preventDefault();
      // postOwnerId is best-effort only (e.g. missing for anonymous
      // questions, whose API response nulls out the author id) — the
      // backend already resolves the real owner from postId when it's
      // absent, so it must not block the click here.
      if (!postId || userId == null) return;
      await handleToggleLike();
    },
    [postId, userId, handleToggleLike]
  );

  const handleCommentSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      const content = String(commentValue || "").trim();
      if (!content || !postId || userId == null) return;

      const tempId = `temp-${Date.now()}`;
      const optimisticComment = {
        _id: tempId,
        id: tempId,
        content,
        text: content,
        createdAt: new Date().toISOString(),
        userId,
        user: resolvedUserObject || post?.user || {
          id: userId,
          fullName: author?.name || "Utilisateur",
          role: author?.role || "Patient",
        },
      };

      setCommentValue("");
      setLocalComments((prev) => [optimisticComment, ...(Array.isArray(prev) ? prev : [])]);

      const dto = {
        content,
        userId,
        postId,
        ...(postOwnerId ? { postOwnerId } : {}),
      };

      try {
        await addComment(dto);
        fetchComments().catch(() => {});
      } catch (err) {
        console.error("Erreur ajout commentaire :", err);
        setLocalComments((prev) => (Array.isArray(prev) ? prev : []).filter((c) => (c?._id || c?.id) !== tempId));
        setCommentValue(content);
      }
    },
    [commentValue, postId, userId, resolvedUserObject, post?.user, author?.name, author?.role, postOwnerId, addComment, fetchComments]
  );

  const fetchTranslation = useCallback(async () => {
    setTranslating(true);
    setTranslationError("");

    try {
      if (typeof onTranslate === "function") {
        const trans = await onTranslate(originalText);
        setTranslatedText(String(trans ?? "").trim() || originalText);
      } else {
        setTranslatedText(originalText);
      }
      setShowTranslation(true);
    } catch (err) {
      setTranslationError(err?.message || t?.("postCard.translationUnavailable") || "Translation unavailable");
    } finally {
      setTranslating(false);
    }
  }, [onTranslate, originalText, t]);

  const startEditComment = useCallback((commentId, initialText) => {
    setEditingCommentId(String(commentId));
    setEditingValue(String(initialText || ""));
    setOpenCommentMenuId(null);
  }, []);

  const cancelEditComment = useCallback(() => {
    setEditingCommentId(null);
    setEditingValue("");
  }, []);

  const saveEditComment = useCallback(
    async (commentId) => {
      const cid = String(commentId);
      const txt = safeStr(editingValue);
      if (!cid || !txt) return;

      setLocalComments((prev) =>
        (Array.isArray(prev) ? prev : []).map((c) =>
          String(c?._id || c?.id) === cid ? { ...c, content: txt, text: txt } : c
        )
      );

      await editComment(cid, txt);
      cancelEditComment();
      fetchComments().catch(() => {});
    },
    [editingValue, editComment, cancelEditComment, fetchComments]
  );

  // ── Ouvre la modal (ne supprime pas encore) ──
  const deleteOneComment = useCallback((commentId) => {
    const cid = String(commentId);
    if (!cid) return;
    setOpenCommentMenuId(null);
    setDeleteConfirm(cid);
  }, []);

  // ── Suppression confirmée depuis la modal ──
  const confirmDeleteComment = useCallback(async () => {
    if (!deleteConfirm) return;
    const cid = String(deleteConfirm);
    setLocalComments((prev) => (Array.isArray(prev) ? prev : []).filter((c) => String(c?._id || c?.id) !== cid));
    await removeComment(cid);
    setDeleteConfirm(null);
    fetchComments().catch(() => {});
  }, [deleteConfirm, removeComment, fetchComments]);

  const handleDoctorCommentClick = useCallback(
    (doctorId) => {
      if (!doctorId) return;
      navigate(DOCTOR_ROUTES.PROFILE_DOCTOR(doctorId));
    },
    [navigate]
  );

  const startEditPost = useCallback(() => {
    setEditingPostValue(String(post?.description || ""));
    setEditingPost(true);
    setShowTranslation(false);
    setMenuOpen(false);
  }, [post?.description]);

  const cancelEditPost = useCallback(() => {
    setEditingPost(false);
    setEditingPostValue("");
  }, []);

  const saveEditPost = useCallback(async () => {
    const text = safeStr(editingPostValue);
    if (!postId || !text || typeof onEdit !== "function") return;

    setSavingPost(true);
    try {
      await onEdit(postId, text);
      cancelEditPost();
    } catch (error) {
      console.error("Erreur lors de la modification de la publication :", error);
    } finally {
      setSavingPost(false);
    }
  }, [editingPostValue, postId, onEdit, cancelEditPost]);

  const confirmDeletePost = useCallback(async () => {
    if (!postId || typeof onDelete !== "function") return;

    setDeletingPost(true);
    try {
      await onDelete(postId);
      setPostDeleteConfirm(false);
    } catch (error) {
      console.error("Erreur lors de la suppression de la publication :", error);
    } finally {
      setDeletingPost(false);
    }
  }, [postId, onDelete]);

  const openPostDeleteConfirm = useCallback(() => {
    setMenuOpen(false);
    setPostDeleteConfirm(true);
  }, []);

  if (!post) {
    return (
      <div className="post-error">
        <span className="error-icon">⚠️</span>
        <span className="error-text">{t?.("postCard.noPostData") || "No post data"}</span>
      </div>
    );
  }

  return (
    <div className="pcard-card">
      <div className="pcard-header">
        <div className="pcard-author-container">
          <div className={`pcard-avatar ${isDoctorPostAuthor ? "pcard-doctor" : "pcard-member"}`}>
            {authorAvatar ? (
              <SafeImage
                debugTag={`post-author-${postId || "x"}`}
                src={authorAvatar}
                alt={author.name}
                className="pcard-avatar-img"
              />
            ) : (
              <InitialsAvatar
                name={author.name}
                fallback={isDoctorPostAuthor ? "D" : "U"}
                className="ch-avatar--placeholder"
                size={40}
              />
            )}
          </div>

          <div className="pcard-author-info">
            <div className="pcard-author-header">
              <span className="pcard-author-name">{author.name}</span>
              {isDoctorPostAuthor && <span className="pcard-badge-doctor">{t?.("postCard.doctor") || "Doctor"}</span>}
              {post.blocked === false && (
                <span className="pcard-badge-verified">{t?.("postCard.verified") || "Verified"}</span>
              )}
            </div>

            <p className="post-time">
              {t?.("homePage.postTime") || "Publié"} {timeAgo(postDate, t)}
            </p>

            <div className="pcard-author-role">{author.role}</div>
          </div>
        </div>

        {isPostOwner && postId && typeof onEdit === "function" && typeof onDelete === "function" && (
          <div className="pcard-menu-wrapper" ref={menuRef}>
            <button
              type="button"
              className="pcard-menu-btn"
              aria-label={t?.("postCard.options") || "Options"}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span className="pcard-menu-dots" aria-hidden="true">⋮</span>
            </button>
            <div className={`pcard-menu${menuOpen ? " open" : ""}`} role="menu">
              <button
                type="button"
                className="pcard-menu-item"
                role="menuitem"
                onClick={startEditPost}
              >
                {t?.("postCard.edit") || "Modifier"}
              </button>
              <button
                type="button"
                className="pcard-menu-item danger"
                role="menuitem"
                onClick={openPostDeleteConfirm}
              >
                {t?.("postCard.delete") || "Supprimer"}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="pcard-content">
        {editingPost ? (
          <div className="pcc-edit">
            <textarea
              className="pcc-edit-input"
              value={editingPostValue}
              onChange={(event) => setEditingPostValue(event.target.value)}
              rows={4}
              autoFocus
              aria-label={t?.("postCard.edit") || "Modifier"}
            />
            <div className="pcc-edit-actions">
              <button
                type="button"
                className="pcc-edit-btn"
                onClick={saveEditPost}
                disabled={savingPost || !safeStr(editingPostValue)}
              >
                {savingPost ? t?.("common.loading") || "Enregistrement…" : t?.("common.save") || "Enregistrer"}
              </button>
              <button
                type="button"
                className="pcc-edit-btn ghost"
                onClick={cancelEditPost}
                disabled={savingPost}
              >
                {t?.("common.cancel") || "Annuler"}
              </button>
            </div>
          </div>
        ) : (
          <p className="pcard-text">
            {showTranslation ? translatedText : originalText}
            {translationError && <span className="pcard-translate-error"> — {translationError}</span>}
          </p>
        )}

        {!editingPost && !!onTranslate && (
          <button
            type="button"
            className="pcard-translate-btn"
            onClick={fetchTranslation}
            disabled={translating || !originalText}
          >
            {translating ? t?.("postCard.translating") || "Translating…" : t?.("postCard.translate") || "Translate"}
          </button>
        )}

        {mediaUrl && (
          <div className="pcard-media">
            <SafeImage
              debugTag={`post-media-${postId || "x"}`}
              src={mediaUrl}
              alt={t?.("postCard.postMediaAlt") || "post media"}
              className="pcard-media-img"
            />
          </div>
        )}
      </div>

      <div className="post-actions">
        <button
          className={`action-button like-button ${liked ? "liked" : ""}`}
          onClick={handleLikeClick}
          disabled={!postId || userId == null}
          aria-pressed={!!liked}
          type="button"
        >
          <img src={liked ? likeIconFilled : likeIconOutline} alt="" aria-hidden="true" width="16" height="17" />
          <span>{t?.("homePage.likePost") || "Aimer la publication"}</span>
        </button>

        <button
          type="button"
          className="action-button comment-button"
          onClick={() => setShowComments((v) => !v)}
          aria-expanded={showComments}
          disabled={!postId}
        >
          <img src={commentIconPrimary} alt="" aria-hidden="true" width="16" height="16" />
          <span>{t?.("homePage.comment") || "Commenter"}</span>
        </button>

        <div className="engagement-stats">
          <button
            type="button"
            className="likes-count-btn"
            onClick={handleLikeClick}
            disabled={!postId || userId == null}
          >
            <img src={liked ? likeIconFilled : likeIconOutline} alt="" aria-hidden="true" width="16" height="17" />
            <span className="likes-count">{Number.isFinite(Number(likeCount)) ? likeCount : 0}</span>
          </button>

          <button
            type="button"
            className="comments-count-btn"
            onClick={() => setShowComments((v) => !v)}
            aria-expanded={showComments}
          >
            <img src={commentIconMuted} alt="" aria-hidden="true" width="16" height="16" />
            <span className="comments-count">{displayCommentsCount}</span>
          </button>
        </div>
      </div>

      {showComments && postId && (
        <div className="pc-comments">
          <div className="pcc-thread">
            <div className="pcc-list">
              {aiResponseText && <AIResponseBanner text={aiResponseText} t={t} />}

              {aiResponseText && Array.isArray(localComments) && localComments.length > 0 && (
                <div className="pcc-ai-divider">
                  <span>
                    {t?.("postCard.memberComments") === "postCard.memberComments"
                      ? "Commentaires des membres"
                      : t?.("postCard.memberComments")}
                  </span>
                </div>
              )}

              {Array.isArray(visibleComments) && visibleComments.length > 0 ? (
                visibleComments.map((c) => {
                  const actor = extractCommentActor(c);
                  const isDoctorAuthor = actor?.type === "doctor";
                  const doctorId = isDoctorAuthor && actor?.id ? String(actor.id) : null;
                  const userId2 = !isDoctorAuthor && actor?.id ? String(actor.id) : null;

                  const authorObj = isDoctorAuthor
                    ? doctorId
                      ? doctorsMap[doctorId]
                      : null
                    : userId2
                    ? usersMap[userId2]
                    : null;

                  return (
                    <CommentRow
                      key={c?._id || c?.id}
                      c={c}
                      actor={actor}
                      authorObj={authorObj}
                      embeddedData={actor?.embedded}
                      isDoctorAuthor={isDoctorAuthor}
                      t={t}
                      viewerId={viewerId}
                      openMenuId={openCommentMenuId}
                      setOpenMenuId={setOpenCommentMenuId}
                      editingId={editingCommentId}
                      editingValue={editingValue}
                      setEditingValue={setEditingValue}
                      onStartEdit={startEditComment}
                      onCancelEdit={cancelEditComment}
                      onSaveEdit={saveEditComment}
                      onDelete={deleteOneComment}
                      onDoctorClick={handleDoctorCommentClick}
                    />
                  );
                })
              ) : (
                !loadingComments && (
                  <div className="pcc-empty">{t?.("homePage.noComments") || "No comments"}</div>
                )
              )}

              {loadingComments && (
                <div className="pcc-more">{t?.("homePage.loadingComments") || "Loading comments…"}</div>
              )}

              {hasMoreThanThreeComments && (
                <div className="pcc-see-all-wrap">
                  <button
                    type="button"
                    className="pcc-see-all-btn"
                    onClick={() => setShowAllComments((v) => !v)}
                  >
                    {showAllComments
                      ? (t?.("postCard.seeLess") === "postCard.seeLess" ? "Voir moins" : t?.("postCard.seeLess"))
                      : (t?.("postCard.seeAllComments") === "postCard.seeAllComments" ? `Voir tous les commentaires (${orderedComments.length})` : t?.("postCard.seeAllComments"))}
                  </button>
                </div>
              )}
            </div>

            <div className="pcc-composer pcc-composer-bottom">
              <form className="pcc-composer-form" onSubmit={handleCommentSubmit}>
                <textarea
                  name="comment"
                  value={commentValue}
                  onChange={(e) => setCommentValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                      e.preventDefault();
                      e.currentTarget.form?.requestSubmit();
                    }
                  }}
                  placeholder={t?.("homePage.writeComment") || "Écrire un commentaire..."}
                  required
                  rows={1}
                  onInput={(e) => {
                    e.target.style.height = "auto";
                    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
                  }}
                />
                <button type="submit" aria-label="Send comment" title="Send">
                  <img className="pcc-soft-icon" src={sendIcon} alt="" aria-hidden="true" />
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal de confirmation de suppression ── */}
      {deleteConfirm && (
        <DeleteConfirmModal
          t={t}
          onConfirm={confirmDeleteComment}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
      {postDeleteConfirm && (
        <DeleteConfirmModal
          t={t}
          title={t?.("postCard.deletePostTitle") || "Supprimer la publication"}
          message={t?.("postCard.deletePostConfirm") || "Voulez-vous vraiment supprimer cette publication ?"}
          isLoading={deletingPost}
          onConfirm={confirmDeletePost}
          onCancel={() => setPostDeleteConfirm(false)}
        />
      )}
    </div>
  );
}